import React, { useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import Preloader from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/Preloader';
import time from 'utils/time';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import seriesColours from '../seriesColours';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

// Chart.js tooltips render to canvas, not the DOM, so CSS wrapping/ellipsis can't apply - a long tag
// name (the "by tag" breakdown has no length limit) has to be truncated here instead.
const TOOLTIP_LABEL_MAX_LENGTH = 18;
const truncateLabel = (label) =>
  label?.length > TOOLTIP_LABEL_MAX_LENGTH ? `${label.slice(0, TOOLTIP_LABEL_MAX_LENGTH)}…` : label;

const EngagementStatsChart = React.forwardRef(
  (
    {
      breakdown,
      labelFor,
      hiddenBuckets,
      typeOfChart,
      currentView,
      countMode,
      isLoading,
      activeSection,
      setActiveSection,
    },
    ref
  ) => {
    const hasData = breakdown?.length > 0 && breakdown.some((item) => item.datapoints?.some((point) => point.value));

    // Mirrors the reverse direction of the chart's own onHover: when activeSection changes because
    // the table was hovered instead, programmatically drive Chart.js's hover/tooltip state to match.
    useEffect(() => {
      const chart = ref?.current;
      if (!chart || !hasData) return;
      const datasets = chart.data?.datasets ?? [];
      if (activeSection == null) {
        chart.setActiveElements([]);
        chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
      } else {
        const elements = datasets.map((_, datasetIndex) => ({ datasetIndex, index: activeSection }));
        chart.setActiveElements(elements);
        chart.tooltip?.setActiveElements(elements, { x: 0, y: 0 });
      }
      chart.update();
    }, [activeSection, hasData]);

    if (isLoading && !hasData) {
      return <Preloader style={{ minHeight: 320 }} />;
    }

    if (!isLoading && !hasData) {
      return (
        <div className='flex-centered justify-content-center' style={{ height: 320 }}>
          <p className='title-h4 main-light-text my-0'>No data available</p>
        </div>
      );
    }

    const labels = breakdown[0].datapoints.map((point) => point.date);
    // Colour (and Chart.js's own dataset index) comes from position in the *full* breakdown list, not
    // whatever's currently visible - otherwise hiding one series reshuffles every colour after it.
    const datasets = breakdown.map((item, index) => {
      const colour = seriesColours[index % seriesColours.length];
      return {
        label: labelFor(item.bucket),
        data: item.datapoints.map((point) => point.value ?? 0),
        // Line mode fills the area under the line at low opacity (matches MentionsChart); Bar mode
        // keeps the bars fully opaque.
        backgroundColor: typeOfChart === 'Line' ? `rgba(${colour}, 0.1)` : `rgb(${colour})`,
        borderColor: `rgb(${colour})`,
        fill: typeOfChart === 'Line',
        // Matches MentionsChart's own borderWidth(): thicker line for Week/Month views, thinner for Day.
        borderWidth: typeOfChart === 'Line' ? (currentView === 'Day' ? 2 : 3) : 0,
        hidden: hiddenBuckets?.includes(item.bucket) ?? false,
      };
    });

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        // Replaced by the custom colour-swatch legend row above the chart.
        legend: { display: false },
        // chartjs-plugin-datalabels registers globally once any page loads it, so every chart needs
        // to explicitly opt out or it inherits on-bar value labels from wherever it was turned on.
        datalabels: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgb(244, 239, 237)',
          borderColor: 'rgba(0, 18, 43, 0.3)',
          borderWidth: 1,
          titleColor: 'rgb(0, 18, 43)',
          bodyColor: 'rgb(0, 18, 43)',
          bodyFont: { weight: 'bold' },
          padding: 12,
          titleFont: { size: 14 },
          displayColors: false,
          footerColor: 'rgb(0, 18, 43)',
          footerMarginTop: 2,
          // Only show rows with a non-zero value for this date - the footer's Total is computed from
          // this same (already-filtered) item list, but that's fine since zero rows add nothing to it.
          filter: (tooltipItem) => tooltipItem.raw !== 0,
          callbacks: {
            title: (context) => time(context?.[0]?.label).formatGeneralDate(false, currentView === 'Month'),
            label: (context) => `${truncateLabel(context.dataset.label)}: ${thousandCommas(context.formattedValue)}`,
            labelTextColor: (context) => context.dataset.borderColor,
            footer: (context) => {
              const total = context.reduce((sum, item) => sum + (item.parsed.y ?? 0), 0);
              const countModeLabel = countMode === 'Touchpoints' ? 'touchpoints' : 'interactions';
              return `Total ${countModeLabel}: ${thousandCommas(total)}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: typeOfChart === 'Bar',
          grid: { display: false },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            callback: function (value) {
              return time(this.getLabelForValue(value)).formatGeneralDate(false, currentView === 'Month');
            },
          },
        },
        y: { stacked: typeOfChart === 'Bar', beginAtZero: true, ticks: { precision: 0 } },
      },
      onHover: (e) => {
        const points = ref?.current?.getElementsAtEventForMode(e, 'nearest', { intersect: false }, true);
        if (points?.length && setActiveSection) {
          setActiveSection(points[0].index);
        }
      },
    };

    return (
      <div className='position-relative' style={{ height: 320 }} onMouseLeave={() => setActiveSection?.(null)}>
        {/* isLoading here means a refetch over already-valid data (the cold-start case returned above
            already) - dim the still-mounted chart in place rather than swapping it out. */}
        {isLoading && <div className='loading-background' />}
        {typeOfChart === 'Line' ? (
          <Line ref={ref} data={{ labels, datasets }} options={options} />
        ) : (
          <Bar ref={ref} data={{ labels, datasets }} options={options} />
        )}
      </div>
    );
  }
);

export default EngagementStatsChart;
